import { isApiError } from "@compliance/domain";
import { Alert, Anchor, Group, Skeleton, Stack, Tabs, Text, Title } from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconChartLine,
  IconFileText,
  IconHistory,
  IconInfoCircle,
  IconPencilPlus,
  IconReportAnalytics,
  IconShieldCheck,
} from "@tabler/icons-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useBatch } from "../api/hooks";
import { BatchStateBadge } from "../components/BatchStateBadge";
import { AuditTab } from "./batch-detail/AuditTab";
import { CorrectionsTab } from "./batch-detail/CorrectionsTab";
import { DataTab } from "./batch-detail/DataTab";
import { IssuesTab } from "./batch-detail/IssuesTab";
import { LifecycleActions } from "./batch-detail/LifecycleActions";
import { Overview } from "./batch-detail/Overview";
import { ReportsTab } from "./batch-detail/ReportsTab";
import { VerificationTab } from "./batch-detail/VerificationTab";
import { Workpaper } from "./batch-detail/Workpaper";

const TAB_VALUES = new Set([
  "overview",
  "workpaper",
  "data",
  "issues",
  "corrections",
  "reports",
  "verification",
  "audit",
]);

export function BatchDetailPage() {
  const { batchId } = useParams();
  const batchQ = useBatch(batchId);
  // Active tab lives in the URL (locked decision: URL is state of record) —
  // otherwise a reload or a shared link carrying Reports-tab state
  // (report/preview/sheet/rfsJob) lands on the hardcoded default tab instead,
  // and — since keepMounted is false — the Reports panel never even mounts to
  // read those params back.
  const [params, setParams] = useSearchParams();
  // Falls back to "reports" (not the hardcoded default) when a Reports-only
  // param is present without an explicit `tab` — a hand-built or pre-existing
  // link that predates this fix still lands on the right tab. An unrecognized
  // `tab` (bookmarked, hand-edited, or stale) falls back the same way rather
  // than being passed straight to Mantine, which would select nothing and
  // render every panel hidden.
  const hasReportsParam = params.has("report") || params.has("rfsJob");
  const defaultTab = hasReportsParam ? "reports" : "workpaper";
  const requestedTab = params.get("tab");
  const activeTab = requestedTab && TAB_VALUES.has(requestedTab) ? requestedTab : defaultTab;
  const setActiveTab = (tab: string | null) => {
    if (!tab) return;
    const p = new URLSearchParams(params);
    p.set("tab", tab);
    setParams(p, { replace: true });
  };

  if (batchQ.isPending) {
    return (
      <Stack gap="lg">
        <Skeleton height={14} width={140} />
        <Skeleton height={36} width={320} />
        <Skeleton height={18} width={420} />
        <Skeleton height={400} radius="md" mt="md" />
      </Stack>
    );
  }

  if (batchQ.isError) {
    const isNotFound = isApiError(batchQ.error) && batchQ.error.httpStatus === 404;
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/batches" size="sm">
          ← Back to batches
        </Anchor>
        <Title order={3}>{isNotFound ? "Batch not found" : "Couldn't load batch"}</Title>
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">
            {isNotFound ? (
              <>
                No batch with ID{" "}
                <Text span ff="monospace">
                  {batchId}
                </Text>
                .
              </>
            ) : (
              batchQ.error.message
            )}
          </Text>
        </Alert>
      </Stack>
    );
  }

  const batch = batchQ.data;

  return (
    <Stack gap="lg">
      <Stack gap={6}>
        <Anchor component={Link} to="/batches" size="sm" c="dimmed">
          ← Back to batches
        </Anchor>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={6} style={{ minWidth: 0 }}>
            <Group gap="md" align="center" wrap="wrap">
              <Title
                order={1}
                ff="monospace"
                fz={{ base: "h3", sm: "h2", md: "h1" }}
                data-tour="detail-title"
              >
                {batch.batch_id}
              </Title>
              <BatchStateBadge state={batch.state} />
            </Group>
            <Group gap="sm" wrap="wrap">
              <Text size="sm" c="dimmed">
                {batch.period_label ?? batch.period_start.slice(0, 7)}
              </Text>
            </Group>
          </Stack>
          <Group gap="xs" wrap="wrap" justify="flex-end">
            <LifecycleActions batch={batch} />
          </Group>
        </Group>
      </Stack>

      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab
            value="overview"
            data-tour="detail-tab-overview"
            leftSection={<IconInfoCircle size={14} />}
          >
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value="workpaper"
            data-tour="detail-tab-workpaper"
            leftSection={<IconReportAnalytics size={14} />}
          >
            Workpaper
          </Tabs.Tab>
          <Tabs.Tab
            value="data"
            data-tour="detail-tab-data"
            leftSection={<IconChartLine size={14} />}
          >
            Data
          </Tabs.Tab>
          <Tabs.Tab
            value="issues"
            data-tour="detail-tab-issues"
            leftSection={<IconAlertTriangle size={14} />}
          >
            Issues
          </Tabs.Tab>
          <Tabs.Tab
            value="corrections"
            data-tour="detail-tab-corrections"
            leftSection={<IconPencilPlus size={14} />}
          >
            Corrections
          </Tabs.Tab>
          <Tabs.Tab
            value="reports"
            data-tour="detail-tab-reports"
            leftSection={<IconFileText size={14} />}
          >
            Reports
          </Tabs.Tab>
          <Tabs.Tab
            value="verification"
            data-tour="detail-tab-verification"
            leftSection={<IconShieldCheck size={14} />}
          >
            Verification
          </Tabs.Tab>
          <Tabs.Tab
            value="audit"
            data-tour="detail-tab-audit"
            leftSection={<IconHistory size={14} />}
          >
            Audit
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <Overview batch={batch} />
        </Tabs.Panel>
        <Tabs.Panel value="workpaper" pt="md">
          <Workpaper batch={batch} />
        </Tabs.Panel>
        <Tabs.Panel value="data" pt="md">
          <DataTab batch={batch} />
        </Tabs.Panel>
        <Tabs.Panel value="issues" pt="md">
          <IssuesTab batch={batch} />
        </Tabs.Panel>
        <Tabs.Panel value="corrections" pt="md">
          <CorrectionsTab batch={batch} />
        </Tabs.Panel>
        <Tabs.Panel value="reports" pt="md">
          <ReportsTab batch={batch} />
        </Tabs.Panel>
        <Tabs.Panel value="verification" pt="md">
          <VerificationTab batch={batch} />
        </Tabs.Panel>
        <Tabs.Panel value="audit" pt="md">
          <AuditTab batch={batch} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
